import { useStableCallback } from "@prismicio/editor-support/React"
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react"

interface GroupProviderProps {
  children: ReactNode
  itemCount: number
  draggable: boolean
  onAddItem?: (atIndex: number) => void
}

export function GroupProvider(props: GroupProviderProps) {
  const { children, itemCount, draggable } = props

  const [activeItem, setActiveItem] = useState<number>()

  const onActiveItemChange = useStableCallback(
    (index: number, active: boolean) => {
      if (active) {
        setActiveItem(index)
      } else if (activeItem === index) {
        setActiveItem(undefined)
      }
    },
  )

  const onAddItem = useStableCallback(props.onAddItem)

  const value = useMemo(
    () => ({ itemCount, draggable, activeItem, onActiveItemChange, onAddItem }),
    [itemCount, draggable, activeItem, onActiveItemChange, onAddItem],
  )

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
}

interface GroupContextValue {
  itemCount: number
  draggable: boolean
  activeItem?: number
  onActiveItemChange: (index: number, active: boolean) => void
  onAddItem?: (atIndex: number) => void
}

const GroupContext = createContext<GroupContextValue | undefined>(undefined)

export function useGroup() {
  const value = useContext(GroupContext)

  if (!value) {
    throw new Error("useGroup must be used within GroupProvider")
  }

  return value
}
